import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmptyState, InlineError, Media, Skeleton } from '../../../components/ui'
import {
  setFavouriteForAll,
  setSameStaff,
  setServiceFavourite,
  setServiceStaff,
  setStaffForAll,
} from '../../../features/booking/selectionSlice'
import { useGetStaffForSelectionQuery } from '../../../services/bookingApi'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { cn } from '../../../utils/cn'
import { useWizardAction } from '../wizardContext'
import type { Staff } from '../../../types/storefront'

/** Everything the customer can say about staff, including "no preference". */
type Mode = 'all' | 'per'

export function SpecialistStep() {
  const { slug, bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selection = useAppSelector((s) => s.selection)

  const branchId = selection.branchId
  const serviceIds = useMemo(() => selection.services.map((s) => s.id), [selection.services])

  const query = useGetStaffForSelectionQuery(
    { slug, branchId: branchId ?? 0, serviceIds },
    { skip: branchId === null || serviceIds.length === 0 },
  )

  const staff = useMemo(() => query.data ?? [], [query.data])

  // Only staff who can take the whole cart can be booked "for everything".
  const forAll = useMemo(
    () => staff.filter((s) => serviceIds.every((id) => s.service_ids.includes(id))),
    [staff, serviceIds],
  )

  const multi = selection.services.length > 1
  const mixed = new Set(selection.services.map((s) => s.staffId)).size > 1
  const [mode, setMode] = useState<Mode>(multi && (mixed || !selection.sameStaff) ? 'per' : 'all')

  const commonStaffId = mixed ? null : (selection.services[0]?.staffId ?? null)

  // "Any specialist" for everything now means one person for the whole visit.
  // With nobody able to take every service there is no such person to find, so
  // the choice would only lead to a day of sold-out times: the visit is handed
  // over to "choose per service" instead.
  const nobodyCoversAll = multi && query.isSuccess && forAll.length === 0
  useEffect(() => {
    if (!nobodyCoversAll) return
    setMode('per')
    if (selection.sameStaff) dispatch(setSameStaff(false))
  }, [nobodyCoversAll, selection.sameStaff, dispatch])

  const pickMode = (next: Mode) => {
    setMode(next)
    // "Any" on the one-specialist tab and "any" on every service ask for two
    // different things, so the tab itself says which one an unnamed cart means.
    if (selection.services.every((s) => s.staffId === null)) dispatch(setSameStaff(next === 'all'))
  }

  // A salon with "no preference" switched off has nobody to hand an unstaffed
  // service to, and POST /bookings refuses one. Hide the choice rather than let
  // the customer carry it three steps to a 422 at the very end.
  const allowAnyStaff = bootstrap.flags.no_preference
  const missingStaff = !allowAnyStaff && selection.services.some((s) => s.staffId === null)

  useWizardAction(
    { label: 'Continue', onClick: () => navigate('../time'), disabled: missingStaff },
    [navigate, missingStaff],
  )

  const chooseForAll = (member: Staff | null) => {
    dispatch(setStaffForAll({ staffId: member?.id ?? null, staffName: member?.name ?? null }))
  }

  /**
   * The heart is frezka's quick-booking gesture, kept whole: tapping it chooses
   * that specialist *and* marks them, and tapping the heart of the one already
   * marked takes the mark back while leaving them chosen. Choosing someone else
   * by the card clears the mark — the slice drops a heart whose specialist was
   * changed — so the booking can never carry a favourite it did not select.
   *
   * It is per service: on the one-specialist tab it marks every service, on the
   * per-service tab only the service it sits under.
   */
  const favouriteForAll = (member: Staff) =>
    commonStaffId === member.id && selection.services.every((s) => s.favourite)

  const toggleFavouriteForAll = (member: Staff) => {
    const wasFavourite = favouriteForAll(member)
    chooseForAll(member)
    dispatch(setFavouriteForAll(!wasFavourite))
  }

  const toggleServiceFavourite = (serviceId: number, member: Staff, choose: () => void) => {
    const service = selection.services.find((s) => s.id === serviceId)
    const wasFavourite = !!service && service.staffId === member.id && service.favourite
    choose()
    dispatch(setServiceFavourite({ serviceId, favourite: !wasFavourite }))
  }

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      <header className="mb-12 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          Choose your specialist
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          {allowAnyStaff
            ? 'Select a preferred artisan for your treatment, or let us pair you with the first available expert.'
            : 'Select the artisan you would like for each treatment.'}
        </p>
        <p className="mt-4 flex items-center gap-2 font-body-md text-body-md text-on-surface-variant">
          <span className="material-symbols-outlined filled text-[18px] text-primary">favorite</span>
          Tap the heart to tell the salon this is the specialist you came for.
        </p>
      </header>

      {multi && (
        <div className="mb-12 flex flex-col gap-2 rounded-full border border-outline-variant p-1 sm:flex-row">
          <ModeTab active={mode === 'all'} onClick={() => pickMode('all')} label="One specialist for everything" />
          <ModeTab active={mode === 'per'} onClick={() => pickMode('per')} label="Choose per service" />
        </div>
      )}

      {query.isLoading ? (
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-12">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} shape="rect" className="h-80 w-full" />
          ))}
        </div>
      ) : query.error ? (
        <InlineError error={query.error} onRetry={query.refetch} />
      ) : mode === 'all' ? (
        <>
          {allowAnyStaff && forAll.length > 0 && (
            <AnyStaffCard
              selected={commonStaffId === null}
              wholeVisit={multi}
              onClick={() => chooseForAll(null)}
            />
          )}

          {forAll.length === 0 ? (
            <EmptyState
              className="mt-12"
              icon="group_off"
              title="No one specialist covers all of these services"
              body="Switch to choosing a specialist for each service."
            />
          ) : (
            <>
              <h2 className="mb-6 font-label-caps text-label-caps uppercase tracking-widest text-on-surface-variant">
                Our specialists
              </h2>
              <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-12 xl:grid-cols-3">
                {forAll.map((member) => (
                  <StaffCard
                    key={member.id}
                    staff={member}
                    selected={commonStaffId === member.id}
                    favourite={favouriteForAll(member)}
                    onClick={() => chooseForAll(member)}
                    onFavourite={() => toggleFavouriteForAll(member)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-16">
          {selection.services.map((service) => {
            const options = staff.filter((s) => s.service_ids.includes(service.id))
            return (
              <section key={service.id}>
                <h2 className="mb-6 border-b border-primary/10 pb-4 font-display text-headline-sm text-primary">
                  {service.name}
                </h2>
                <div className="grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-10">
                  {allowAnyStaff && (
                    <AnyStaffTile
                      selected={service.staffId === null}
                      onClick={() =>
                        dispatch(setServiceStaff({ serviceId: service.id, staffId: null, staffName: null }))
                      }
                    />
                  )}
                  {options.map((member) => {
                    const choose = () =>
                      dispatch(
                        setServiceStaff({
                          serviceId: service.id,
                          staffId: member.id,
                          staffName: member.name,
                        }),
                      )
                    return (
                      <StaffTile
                        key={member.id}
                        staff={member}
                        selected={service.staffId === member.id}
                        favourite={service.staffId === member.id && service.favourite}
                        onClick={choose}
                        onFavourite={() => toggleServiceFavourite(service.id, member, choose)}
                      />
                    )
                  })}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ModeTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex-1 rounded-full px-6 py-3 font-label-caps text-label-caps uppercase transition-colors duration-300',
        active ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:text-primary',
      )}
    >
      {label}
    </button>
  )
}

/** "Any specialist" is a first-class choice, not a fallback: it is the widest
 *  availability the customer can ask for, so it leads the screen. */
function AnyStaffCard({
  selected,
  wholeVisit,
  onClick,
}: {
  selected: boolean
  wholeVisit: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'group mb-12 flex w-full items-center justify-between gap-6 rounded-xl border p-6 text-left transition-colors duration-300',
        selected
          ? 'border-primary bg-primary/5'
          : 'border-primary/20 bg-surface hover:border-primary/50 hover:bg-surface-container-low',
      )}
    >
      <span className="flex items-center gap-6">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-fixed transition-transform duration-300 group-hover:scale-105">
          <span className="material-symbols-outlined text-[32px] text-on-primary-fixed">group</span>
        </span>
        <span className="min-w-0">
          <span className="mb-1 block font-display text-headline-sm text-on-surface">Any available specialist</span>
          <span className="block font-body-md text-body-md text-on-surface-variant">
            {wholeVisit
              ? 'We will assign one specialist who can do everything and is free for your whole visit.'
              : 'We will assign a specialist who is free at your chosen time.'}
          </span>
        </span>
      </span>
      <SelectionDot selected={selected} size="lg" />
    </button>
  )
}

/**
 * Frezka's quick booking puts a heart on each staff card — "I want the service
 * with this staff!" — and carries it into the booking, where the dashboard
 * calendar shows it to the front desk. It is its own button, sitting opposite
 * the Selected badge, so the card itself stays one tap to choose.
 */
function FavouriteButton({
  active,
  onClick,
  className,
}: {
  active: boolean
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={active ? 'Remove preferred specialist' : 'I want the service with this specialist'}
      title={active ? 'Your preferred specialist' : 'I want the service with this specialist!'}
      className={cn(
        'absolute z-10 flex h-9 w-9 items-center justify-center rounded-full bg-surface/90 shadow-sm transition-colors duration-300 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
        active ? 'text-primary' : 'text-outline',
        className,
      )}
    >
      <span className={cn('material-symbols-outlined text-[20px]', active && 'filled')}>favorite</span>
    </button>
  )
}

/**
 * The artisan card is the character of this step: a 4/3 portrait over a
 * label-caps role and a name in the display face, with the choice shown both as
 * a dot and — once picked — as a badge over the photograph.
 */
function StaffCard({
  staff,
  selected,
  favourite,
  onClick,
  onFavourite,
}: {
  staff: Staff
  selected: boolean
  favourite: boolean
  onClick: () => void
  onFavourite: () => void
}) {
  return (
    <div
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-xl border transition-colors duration-300',
        selected ? 'border-primary ring-1 ring-primary' : 'border-primary/10 bg-surface hover:border-primary/50',
      )}
    >
      <FavouriteButton active={favourite} onClick={onFavourite} className="left-4 top-4" />
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className="flex h-full w-full flex-col text-left"
      >
        <span className="relative block aspect-[4/3] w-full overflow-hidden bg-tertiary-fixed">
          <Media
            src={staff.avatar}
            alt=""
            kind="person"
            className="transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 border border-on-secondary/10" />
          {selected && (
            <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-primary px-3 py-1 font-label-caps text-[10px] uppercase tracking-widest text-on-primary backdrop-blur-md">
              <span className="material-symbols-outlined text-[14px]">check</span>
              Selected
            </span>
          )}
        </span>

        <span className={cn('flex flex-1 flex-col p-6', selected && 'bg-primary/5')}>
          <span className="flex items-start justify-between gap-4">
            <span className="min-w-0">
              <span className="block truncate font-display text-headline-sm text-on-surface">{staff.name}</span>
              {staff.title && (
                <span className="mt-1 block truncate font-label-caps text-label-caps uppercase text-primary">
                  {staff.title}
                </span>
              )}
            </span>
            <SelectionDot selected={selected} />
          </span>

          {favourite && (
            <span className="mt-4 flex items-center gap-2 font-label-caps text-label-caps uppercase text-primary">
              <span className="material-symbols-outlined filled text-[16px]">favorite</span>
              The specialist you came for
            </span>
          )}

          {staff.rating !== null && (
            <span className="mt-auto flex items-center gap-2 pt-6 font-label-caps text-label-caps uppercase text-on-surface-variant">
              <span className="material-symbols-outlined filled text-[16px] text-primary">star</span>
              {staff.rating.toFixed(1)} rating
            </span>
          )}
        </span>
      </button>
    </div>
  )
}

function AnyStaffTile({ selected, onClick }: { selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'flex flex-col items-center gap-3 rounded-xl border p-6 text-center transition-colors duration-300',
        selected ? 'border-primary bg-primary/5' : 'border-primary/10 bg-surface hover:border-primary/50',
      )}
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-fixed">
        <span className="material-symbols-outlined text-[28px] text-on-primary-fixed">group</span>
      </span>
      <span className="font-body-md text-body-md text-on-surface">Any specialist</span>
    </button>
  )
}

function StaffTile({
  staff,
  selected,
  favourite,
  onClick,
  onFavourite,
}: {
  staff: Staff
  selected: boolean
  favourite: boolean
  onClick: () => void
  onFavourite: () => void
}) {
  return (
    <div
      className={cn(
        'relative rounded-xl border transition-colors duration-300',
        selected ? 'border-primary bg-primary/5' : 'border-primary/10 bg-surface hover:border-primary/50',
      )}
    >
      <FavouriteButton active={favourite} onClick={onFavourite} className="right-2 top-2 h-8 w-8" />
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className="flex w-full flex-col items-center gap-3 p-6 text-center"
      >
        <span className="h-16 w-16 overflow-hidden rounded-full bg-tertiary-fixed">
          <Media src={staff.avatar} alt="" kind="person" />
        </span>
        <span className="min-w-0">
          <span className="block truncate font-body-md text-body-md text-on-surface">{staff.name}</span>
          {staff.title && (
            <span className="mt-1 block truncate font-label-caps text-label-caps uppercase text-primary">
              {staff.title}
            </span>
          )}
        </span>
      </button>
    </div>
  )
}

function SelectionDot({ selected, size = 'md' }: { selected: boolean; size?: 'md' | 'lg' }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        size === 'lg' ? 'h-8 w-8' : 'h-6 w-6',
        selected ? 'border-primary bg-primary' : 'border-outline-variant group-hover:border-primary',
      )}
    >
      {selected && <span className={cn('rounded-full bg-on-primary', size === 'lg' ? 'h-3 w-3' : 'h-2.5 w-2.5')} />}
    </span>
  )
}
